import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const contentData: ContentData[][] = [
  [
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/mss/interview1.webp',
      alt: 'インタビュー写真',
      text: 'アート引越センター株式会社 不動産法人営業部 法人営業1課 係長 歌津 一明さん',
    },
    {
      type: 'sectionTitle',
      text: '膨大な作業が必要とされる、新築マンションの引越の課題',
    },
    {
      type: 'textBox',
      texts: ['―― 今回、どのような経緯で一斉入居をデジタル化することにしたのでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '30〜40年ほど前に大型マンションが建てられ始め、さまざまな問題に直面して参りました。',
        '従来の一斉入居では、膨大な作業量が必要となっていました。具体的には、一斉入居のお知らせ案内の書類をご送付し、返信封筒でご希望日を返送いただき、期日までに申し込んでいただけないお客さまにはお電話をして、返送いただいた書類をすべて開封し、ご希望日を整理して抽選を行い……といった具合です。また、このやり方では膨大な量の紙を使用します。これまでは何十年も同じやり方を続けてきたわけですが、現代は何でも「スマホで完結できないか？」と発想する時代ですので、お客さまがスマホで簡単にご利用いただける仕組みができないかと考えるようになりました。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― デジタル化によって、どのようなメリットが生まれるのでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'デジタル化することで、わずらわしい手間と時間が削減でき、お客さまのスピーディーな対応もできる上、環境への配慮もできます。',
        'また、「どのくらいのお客さまからレスポンスがあり、どのお客さまがいつ引越すのかを、リアルタイムに可視化する」システムにしたいと考えていました。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― そんなデジタル化にあたり複数の開発会社にお声がけされたと思いますが、Aitriaにご依頼いただいた理由を教えていただけますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'たくさんの企業さんにオファーを出させていただく中で、アイスリーさんのレスポンスが良く熱意が感じられたのが大きなポイントでした。これまでいくつかの社内システム構築において、引越業界の特性を開発会社さんに理解していただくのに時間を要してきました。それに対し、アイスリーさんは最初から我々の業務を深く知ろうという姿勢を見せてくれました。それに安心感を覚え、「これはかなり上手くいくのではないか」という期待感がもてました。',
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'デジタルの常識と引越業界の常識をすり合わせたプロジェクト',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/mss/interview2.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― 実際にAitriaとプロジェクトを進められた印象はいかがでしたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '期待以上の結果でした。私たちが最初にイメージしていたのは、今ある業務をうまくデジタル化することでした。しかし、アイスリーさんからは「このままではデジタル化にそぐわない部分がある」とご提案をいただいたり、「お客さまの利便性を考えるともっと平易にすべき」といったご指摘をいただいたりしました。アイスリーさんは引越業界の当たり前を見直して、フラットに世の中のスタンダードと比較して意見をしていただけたので、すごくありがたかったです。',
        'また、週1回のペースで開発中のデザインを見せていただき、こちらが要求しているイメージと齟齬があれば都度それを一つずつ潰していきながら作ることができました。これまでの開発では、事前に細かい要件定義を活字に起こしたとしても、その受け止め方はお互いに異なっており齟齬が生じることがたくさんありましたが、開発段階で確認できたことでそういったロスは発生しませんでした。 それらのセッションを繰り返すことで、信頼関係を構築しながら開発できたと考えています。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― 弊社としても、引越に関する専門知識を必要とする箇所において、アートさんと密にコミュニケーションをさせていただき修正を重ねることができたのが印象的でした。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'とくに抽選アルゴリズムにおいては、タワーマンションは1階にはお部屋がなかったり、エレベーターや搬入経路の数に応じて調整が入ったりなど、業界特有の知識に基づく設計が必要でしたからね。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 従来のデジタル化以前は、どのようにそうした計算をしていたのでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '担当が自力で計算していました。マンションの図面で経路と部屋の数を確認し、これまでの経験から計算をしていたのです。そのため非常に手間がかかるだけでなく、作業者の熟練度が求められました。さらに部屋数が膨大になってくると、どれだけチェックを重ねてもヒューマンエラーが起こってしまう可能性をゼロにはできませんでした。それが今回のデジタル化で、我々も本当に安心して業務を行うことができるようになりました。',
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '「頼みたい」このプロダクトのおかげで言われた言葉',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/mss/interview3.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― ローンチしたプロダクトの評判はいかがでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'まず、不動産販売会社さんからは非常に好評をいただいており、このプロダクトがあるから「幹事はアートさんにおまかせします」と言っていただける流れが実際にあります。私自身もまさにこのシステムを持って営業をかけてもいるのですが、皆さん「もう待ってました」といった反応です。やはり不動産を実際に販売している会社様からすると、このシステムが自分たちの業務にどれだけプラスになるのかイメージができるので「やっとこういうデジタル化の流れが、引越業界さんにも来たんだね」と言葉をかけていただくことも少なくありません。',
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/mss/interview4.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― 引越会社のみならず、業界全体として待望されていたプロダクトだったわけですね。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'はい。我々はこの業界のリーディングカンパニーとして、引越業界全体のサービスレベルを上げていくために常に課題を見つけて、それを改善したいと考えています。今回の一斉入居のデジタル化についても、他の企業も我々のやったことを見てそれ以上のものを作ろうとするなどして、健全な競争が生まれればいいと考えています。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 最後に、本プロダクトの今後の展望について伺えますでしょうか。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '本プロダクトによって弊社が幹事会社としてより多くの受注をいただけているわけですが、当社が全部の物件を担当できるかというと生産量的に問題があります。業界全体でよりお客さまに分かりやすく業務を効率化していくためにも、他の企業でもこのシステムを使えるようにする展開も検討しております。',
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/mss/interview5.webp',
      alt: 'インタビュー写真',
    },
  ],
]

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()
  // const container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 80%',
      toggleActions: 'play none none reverse',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
